import type { ElementResult, FamilyDefinition } from '@batixa/engine';

export type Params = Record<string, string>;

export interface ProjectSettings {
  vatEnabled: boolean;
  vatRate: string;
  defaultLossRate: string;
  contingencyRate: string;
  contingencyBase: 'DIRECT_COST' | 'MATERIALS';
  siteOverheadRate: string;
  generalOverheadRate: string;
  marginRate: string;
  rebarCommercialLength: string;
}

export interface Project {
  id: string;
  reference: string;
  name: string;
  clientName: string | null;
  ownerName: string | null;
  contractorName: string | null;
  engineerName: string | null;
  architectName: string | null;
  location: string | null;
  projectDate: string | null;
  buildingType: string | null;
  grossArea: string | null;
  description: string | null;
  observations: string | null;
  currency: string;
  status: 'DRAFT' | 'IN_PROGRESS' | 'DONE' | 'ARCHIVED';
  pricingMode: 'CONTRACTOR' | 'DIRECT_LABOR' | 'BOTH';
  version: number;
  updatedAt: string;
  settings: ProjectSettings;
}

export interface ProjectListItem {
  id: string;
  reference: string;
  name: string;
  clientName: string | null;
  location: string | null;
  status: Project['status'];
  updatedAt: string;
  levelCount: number;
  typeCount: number;
}

export interface Zone { id: string; code: string; name: string; kind: string; sortOrder: number; version: number }
export interface Level { id: string; zoneId: string; code: string; name: string; kind: string; height: string | null; sortOrder: number; version: number }
export interface Family { id: string; code: string; name: string; group: string; definition: FamilyDefinition; companyId?: string | null }
export interface ElementType { id: string; familyId: string; zoneId: string | null; code: string; name: string | null; params: Params; resources?: import('@batixa/engine').TypeResources; version: number }
export interface Variant { id: string; typeId: string; levelId: string; params: Params }
export interface Segment { id: string; label: string; length: string; params: Params }
export interface Opening { id: string; code: string | null; width: string; height: string; count: string }
export interface Instance {
  id: string;
  typeId: string;
  levelId: string;
  mode: 'AGGREGATE' | 'DETAILED';
  quantity: string;
  label: string | null;
  params: Params;
  quantitySource: string;
  version: number;
  segments: Segment[];
  openings: Opening[];
  result: ElementResult;
}
export type KindTotals = Record<string, string>;

export interface Link { id: string; sourceInstanceId: string; targetInstanceId: string; sourceOutputKey: string; targetOutputKey: string; factor: string; note: string | null }

export interface Structure {
  links: Link[];
  linkProblems: Array<{ linkId: string; message: string }>;
  backfill: { value: string; excavation: string; buried: string; steps: string[]; warning?: string };
  zones: Zone[];
  levels: Level[];
  families: Family[];
  types: ElementType[];
  variants: Variant[];
  instances: Instance[];
  totals: { byLevel: Record<string, KindTotals>; byZone: Record<string, KindTotals>; project: KindTotals };
}

export const KIND_LABELS: Record<string, { label: string; unit: string }> = {
  CONCRETE: { label: 'Béton', unit: 'm³' },
  FORMWORK: { label: 'Coffrage', unit: 'm²' },
  BLINDING: { label: 'Béton de propreté', unit: 'm³' },
  EXCAVATION: { label: 'Fouilles', unit: 'm³' },
  BACKFILL: { label: 'Remblai', unit: 'm³' },
  MASONRY: { label: 'Maçonnerie', unit: 'm²' },
  MASONRY_VOLUME: { label: 'Volume maçonnerie', unit: 'm³' },
  AREA: { label: 'Surfaces', unit: 'm²' },
  LENGTH: { label: 'Linéaires', unit: 'ml' },
};

export const GROUP_LABELS: Record<string, string> = {
  FOUNDATION: 'Fondations',
  VERTICAL: 'Structure verticale',
  HORIZONTAL: 'Structure horizontale',
  MASONRY: 'Maçonnerie',
  FINISH: 'Finitions',
  ROOF: 'Toiture',
  EARTHWORK: 'Terrassement',
  OTHER: 'Autres',
};

export const STATUS_LABELS: Record<Project['status'], string> = {
  DRAFT: 'Brouillon',
  IN_PROGRESS: 'En cours',
  DONE: 'Terminé',
  ARCHIVED: 'Archivé',
};
